import { toaster } from '@/components/ui/toaster';
import {
  invalidateMentionBoostSetupQueries,
  updateMentionBoostSetupQueries,
} from '@/hooks/queries/use-mention-boost-setup-query';
import { invalidateCoPublicitesQueries } from '@/hooks/queries/use-co-publicites-query';
import api from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import type {
  ApiErrorResponse,
  ApiResponse,
  SaveMentionBoostSetupRequest,
  SaveMentionBoostSetupResponse,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

interface SaveMentionBoostSetupVariables extends SaveMentionBoostSetupRequest {
  mentionId: string;
}

const saveMentionBoostSetup = async ({
  mentionId,
  ...payload
}: SaveMentionBoostSetupVariables): Promise<ApiResponse<SaveMentionBoostSetupResponse>> => {
  const response = await api.put<ApiResponse<SaveMentionBoostSetupResponse>>(
    `/dashboard/mentions/${mentionId}/boost-setup`,
    payload
  );

  return response.data;
};

export const useSaveMentionBoostSetup = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<SaveMentionBoostSetupResponse>,
    AxiosError<ApiErrorResponse>,
    SaveMentionBoostSetupVariables
  >({
    mutationFn: saveMentionBoostSetup,
    // Saves are silent on success: the draft persists in the background while the
    // marketer keeps editing, so there is no success toast. The cache is primed
    // and reconciled, and failures still surface through the onError toast below.
    onSuccess: (response, variables) => {
      const saved = response.data;
      // Merge, never replace: the save response carries only `setup` + `eligibility`,
      // while the cache also holds `readiness` and `launchedBoost`. Writing the
      // response wholesale would drop those until the refetch lands, briefly
      // emptying the ad-account set (launch CTA flicker, and a multi-account brand
      // could confirm a launch against the default account instead of the selected
      // one). Preserve the rest of the cached shape.
      if (saved) {
        updateMentionBoostSetupQueries(queryClient, variables.mentionId, (previous) =>
          previous
            ? { ...previous, setup: saved.setup, eligibility: saved.eligibility }
            : {
                setup: saved.setup,
                eligibility: saved.eligibility,
                readiness: null,
                launchedBoost: null,
              }
        );
      }
      void invalidateMentionBoostSetupQueries(queryClient, variables.mentionId);
      void invalidateCoPublicitesQueries(queryClient);
    },
    onError: (error) => {
      toaster.error({
        title: t('common.error'),
        description: getApiErrorMessage(error, t, 'common.error'),
      });
    },
  });
};
